import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PublicNav, Footer } from "@/components/PublicChrome";
import { Embers } from "@/components/Embers";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Hourglass, XCircle, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export const PaymentSuccessPage = () => {
  const [params] = useSearchParams();
  const orderId = params.get("orderId");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    if (!orderId) return;
    const fetch = () => api.get(`/payments/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});
    fetch();
    const i = setInterval(fetch, 10000);
    return () => clearInterval(i);
  }, [orderId]);

  return (
    <div className="min-h-screen bg-[#050505] text-[#f5f5f5]">
      <PublicNav />
      <section className="relative py-24 overflow-hidden">
        <Embers count={20} />
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[460px] h-[460px] bg-[#ff7a00]/15 rounded-full blur-[140px]" />
        <div className="max-w-2xl mx-auto px-6 text-center relative" data-testid="payment-success-page">
          <CheckCircle2 className="mx-auto text-emerald-400" size={56} />
          <h1 className="font-display text-4xl sm:text-5xl font-bold mt-6">Payment received.</h1>
          <p className="text-[#8a8a8a] mt-3 max-w-md mx-auto">
            We're confirming your transaction. Once confirmed, our admin team provisions your trading account within hours.
          </p>
          {order && (
            <div className="glass rounded-xl p-6 mt-8 text-left max-w-md mx-auto">
              <div className="text-[10px] text-[#8a8a8a] uppercase tracking-widest">{order.challenge_name}</div>
              <div className="font-display text-2xl font-bold mt-1">${order.account_size.toLocaleString()}</div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[10px] text-[#8a8a8a] uppercase tracking-wider">Status</div>
                  <div className="text-[#ff9a2f] font-semibold mt-1">{order.status}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#8a8a8a] uppercase tracking-wider">Payment</div>
                  <div className="text-[#cfcfcf] mt-1">{order.payment_status || "Awaiting"}</div>
                </div>
              </div>
            </div>
          )}
          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/dashboard" data-testid="success-dashboard-link">
              <Button className="bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold">
                Go to Dashboard <ArrowRight size={16} className="ml-1.5" />
              </Button>
            </Link>
            <Link to="/dashboard/orders" data-testid="success-orders-link">
              <Button variant="outline" className="border-[#ff7a00]/30 text-[#cfcfcf] hover:bg-[#ff7a00]/10 hover:text-[#ff9a2f]">View Orders</Button>
            </Link>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export const PaymentCancelPage = () => (
  <div className="min-h-screen bg-[#050505] text-[#f5f5f5]">
    <PublicNav />
    <section className="relative py-24">
      <div className="max-w-xl mx-auto px-6 text-center" data-testid="payment-cancel-page">
        <XCircle className="mx-auto text-red-400" size={48} />
        <h1 className="font-display text-3xl sm:text-4xl font-bold mt-6">Payment cancelled.</h1>
        <p className="text-[#8a8a8a] mt-3">No charges were made. You can try again any time.</p>
        <Link to="/challenges" data-testid="cancel-back-link">
          <Button className="mt-6 bg-[#ff7a00] hover:bg-[#ff9a2f] text-[#050505] font-semibold">Back to Challenges</Button>
        </Link>
      </div>
    </section>
    <Footer />
  </div>
);
